"use client";

import Image from "next/image";
import { ArrowRight } from "lucide-react";

/**
 * Reusable App Banner Showcase Section
 * Desktop/Tablet: background image with left-aligned content
 * Mobile: gradient background (no image), full-width content
 * @param {string} title - Banner title
 * @param {string} description - Banner description
 * @param {string} ctaText - Button text
 * @param {string} ctaLink - Button link
 * @param {string} backgroundImage - Background image path (desktop/tablet only)
 */
export const AppBannerShowcase = ({
  title,
  description,
  ctaText = "Install now",
  ctaLink = "#",
  secondaryCtaText,
  secondaryCtaLink,
  backgroundImage,
}) => {
  return (
    <section className="relative z-10 bg-[#1e1e1e]">
      <div className="mx-auto px-4 md:px-16 lg:px-32 pb-4 md:pb-16 max-w-[1440px]">
        {/* Desktop/Tablet - with background image */}
        <div
          className="hidden md:flex relative rounded-2xl border border-white/10 overflow-hidden max-w-[1184px] mx-auto p-16"
        >
          {/* Background Image */}
          {backgroundImage && (
            <Image
              src={backgroundImage}
              alt=""
              fill
              sizes="100vw"
              className="object-cover pointer-events-none"
              priority={false}
            />
          )}

          {/* Content */}
          <div className="relative z-10 flex flex-col gap-6 max-w-[50%] lg:max-w-[60%]">
            <div className="flex flex-col gap-6">
              <h2
                className="text-2xl lg:text-[40px] font-normal text-white tracking-[-0.72px] lg:tracking-[-1.2px]"
                style={{ lineHeight: 1.3 }}
              >
                {title}
              </h2>
              <p
                className="text-white/60 text-base"
                style={{ lineHeight: 1.4 }}
              >
                {description}
              </p>
            </div>
            <div className="flex flex-wrap items-stretch gap-3">
              <a
                href={ctaLink}
                className="group inline-flex items-center h-[52px] bg-[#dbdacd] border border-[#f8f7f4] rounded-[10px] overflow-hidden px-4 shadow-[inset_0px_0px_0px_0px_transparent] hover:bg-[#1e1e1e] hover:border-[#606060] hover:shadow-[inset_0px_0px_8.7px_1px_#bfbeb8] transition-[background-color,border-color,box-shadow] duration-500 ease-in-out"
              >
                <span
                  className="text-base font-medium text-[#3e3e3e] group-hover:text-[#dbdacd] tracking-[-0.144px] px-2 transition-colors duration-500"
                  style={{ lineHeight: "20px" }}
                >
                  {ctaText}
                </span>
                <ArrowRight className="w-5 h-5 text-[#3e3e3e] group-hover:text-[#dbdacd] transition-colors duration-500" />
              </a>
              {secondaryCtaText && secondaryCtaLink && (
                <a
                  href={secondaryCtaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center h-[52px] bg-[#1E1E1E] border border-[rgba(96,96,96,0.4)] rounded-[10px] overflow-hidden px-4 shadow-[inset_0px_0px_0px_0px_transparent] hover:border-[#606060] hover:shadow-[inset_0px_0px_8.7px_1px_#bfbeb8] transition-[box-shadow,border-color] duration-500 ease-in-out"
                >
                  <span
                    className="text-base font-medium text-[#BFBEB8] tracking-[-0.144px] px-2"
                    style={{ lineHeight: "20px" }}
                  >
                    {secondaryCtaText}
                  </span>
                  <ArrowRight className="w-5 h-5 text-[#BFBEB8]" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Mobile - gradient background, no image */}
        <div
          className="flex md:hidden rounded-2xl border border-white/10 overflow-hidden max-w-[1184px] mx-auto p-4"
          style={{
            backgroundImage:
              "linear-gradient(93.5deg, #363636 0.17%, #1e1e1e 100%)",
          }}
        >
          <div className="flex flex-col gap-6 flex-1">
            <div className="flex flex-col gap-6">
              <h2
                className="text-[30px] font-normal text-white tracking-[-0.9px] w-full"
                style={{ lineHeight: 1.3 }}
              >
                {title}
              </h2>
              <p
                className="text-white/60 text-base tracking-[-0.032px] w-full"
                style={{ lineHeight: "24px" }}
              >
                {description}
              </p>
            </div>
            <div className="flex flex-col items-stretch gap-3">
              <a
                href={ctaLink}
                className="group inline-flex items-center justify-center h-[52px] bg-[#dbdacd] border border-[#f8f7f4] rounded-[10px] overflow-hidden px-4 shadow-[inset_0px_0px_0px_0px_transparent] hover:bg-[#1e1e1e] hover:border-[#606060] hover:shadow-[inset_0px_0px_8.7px_1px_#bfbeb8] transition-[background-color,border-color,box-shadow] duration-500 ease-in-out"
              >
                <span
                  className="text-base font-medium text-[#3e3e3e] group-hover:text-[#dbdacd] tracking-[-0.144px] px-2 transition-colors duration-500"
                  style={{ lineHeight: "20px" }}
                >
                  {ctaText}
                </span>
                <ArrowRight className="w-5 h-5 text-[#3e3e3e] group-hover:text-[#dbdacd] transition-colors duration-500" />
              </a>
              {secondaryCtaText && secondaryCtaLink && (
                <a
                  href={secondaryCtaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center h-[52px] bg-[#1E1E1E] border border-[rgba(96,96,96,0.4)] rounded-[10px] overflow-hidden px-4 shadow-[inset_0px_0px_0px_0px_transparent] hover:border-[#606060] hover:shadow-[inset_0px_0px_8.7px_1px_#bfbeb8] transition-[box-shadow,border-color] duration-500 ease-in-out"
                >
                  <span
                    className="text-base font-medium text-[#BFBEB8] tracking-[-0.144px] px-2"
                    style={{ lineHeight: "20px" }}
                  >
                    {secondaryCtaText}
                  </span>
                  <ArrowRight className="w-5 h-5 text-[#BFBEB8]" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
